const DEFAULT_CHUNK_SIZE = 8 * 1024 * 1024;
export const MAX_FILE_SIZE = 384 * 1024 * 1024;

function headers(inbox, extra = {}) {
  return { "X-EMX-Inbox": inbox, ...extra };
}

async function parseResponse(response) {
  if (response.ok) {
    if (response.status === 204) return null;
    return response.json();
  }
  let message = `Request failed (${response.status}).`;
  try {
    const payload = await response.json();
    if (payload?.error) message = payload.error;
  } catch {}
  throw new Error(message);
}

async function retry(task, attempts = 3) {
  let lastError;
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      return await task();
    } catch (error) {
      lastError = error;
      if (attempt < attempts) await new Promise((resolve) => setTimeout(resolve, 450 * attempt));
    }
  }
  throw lastError;
}

export async function uploadFile({ inbox, direction, file, onProgress, signal }) {
  if (!file || file.size < 1) throw new Error("Choose a file that is not empty.");
  if (file.size > MAX_FILE_SIZE) throw new Error("Files must be 384 MB or smaller.");

  const start = await fetch("/api/transfers", {
    method: "POST",
    headers: headers(inbox, { "Content-Type": "application/json" }),
    body: JSON.stringify({
      direction,
      name: file.name,
      contentType: file.type || "application/octet-stream",
      size: file.size,
    }),
    signal,
  }).then(parseResponse);

  const chunkSize = start.chunkSize || DEFAULT_CHUNK_SIZE;
  const parts = [];
  try {
    for (let index = 0; index < start.parts; index += 1) {
      const partNumber = index + 1;
      const chunk = file.slice(index * chunkSize, Math.min(file.size, (index + 1) * chunkSize));
      const part = await retry(async () => {
        const response = await fetch(`/api/transfers/${start.id}/parts/${partNumber}`, {
          method: "PUT",
          headers: headers(inbox, { "Content-Type": "application/octet-stream" }),
          body: chunk,
          signal,
        });
        return parseResponse(response);
      });
      parts.push(part);
      onProgress?.(Math.round((partNumber / start.parts) * 100));
    }

    return await fetch(`/api/transfers/${start.id}/complete`, {
      method: "POST",
      headers: headers(inbox, { "Content-Type": "application/json" }),
      body: JSON.stringify({ parts }),
      signal,
    }).then(parseResponse);
  } catch (error) {
    await fetch(`/api/transfers/${start.id}`, {
      method: "DELETE",
      headers: headers(inbox),
    }).catch(() => {});
    throw error;
  }
}

export async function listTransfers(inbox, direction, signal) {
  return fetch(`/api/transfers?direction=${encodeURIComponent(direction)}`, {
    headers: headers(inbox),
    cache: "no-store",
    signal,
  }).then(parseResponse);
}

export async function deleteTransfer(inbox, id) {
  return fetch(`/api/transfers/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: headers(inbox),
  }).then(parseResponse);
}

export async function downloadTransfer(inbox, item) {
  const response = await fetch(`/api/transfers/${encodeURIComponent(item.id)}/content`, {
    headers: headers(inbox),
    cache: "no-store",
  });
  if (!response.ok) return parseResponse(response);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = item.name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

export async function readNote(inbox, item, signal) {
  if (item.size > 256 * 1024) throw new Error("This note is too large to preview. Download it to read it.");
  const response = await fetch(`/api/transfers/${encodeURIComponent(item.id)}/content`, {
    headers: headers(inbox), cache: "no-store", signal,
  });
  if (!response.ok) return parseResponse(response);
  const data = await response.json();
  if (data?.type !== "emx-note" || typeof data.text !== "string") throw new Error("This file is not a readable EMX note. Download it instead.");
  return { title: typeof data.title === "string" ? data.title : item.name, text: data.text };
}

export function formatBytes(bytes) {
  const units = ["B", "KB", "MB", "GB"];
  let value = Number(bytes) || 0;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 10 || unit === 0 ? 0 : 1)} ${units[unit]}`;
}
